// Route function for `/<orgSlug>/<scrollySlug>` (Master-Briefing v5 Z. 435
// Backwards-Compatibility-DoD).
//
// Strict match against Vike's filesystem catch-all: this Vike-Page lives
// at `pages/@orgSlug/@scrollySlug/`, which would otherwise match every
// two-segment URL — including `/scrolly/<publicKey>` (Phase-4 CP1) and
// `/s/<slug>` (legacy). We use a `+route.ts` to:
//
//   1. Exclude reserved top-level segments that belong to other Vike
//      pages (s, scrolly, category, preview, __dev__, _error)
//   2. Exclude `@`-prefixed segments that belong to the `@handle/`
//      creator-profile route
//   3. Require exactly two non-empty path segments (optional trailing
//      slash)
//
// Test coverage for both positive and negative cases lives in
// `+route.test.ts` alongside this file.
//
// Drift-Resolution 2026-05-11 (CP-Final): `scrollytelling` was initially
// in the reserved list (CP2), under the false assumption that
// `/scrollytelling/<orgSlug>/<scrollySlug>` was a 3-segment route per
// Master-Briefing v5. Smoke-test against the real production URL
// `viewer.scrolly.com/scrollytelling/warpowersresolution` (live today
// in app/-Stack as 2-segment with orgSlug=scrollytelling) showed a 404.
// Master-Briefing v5 Z. 385/410/435 actually specifies 2-segment URL
// patterns only — `scrollytelling` is the default org-slug for the
// Scrollytelling-GmbH organisation, NOT a reserved Vike segment. The
// 3-segment route has been removed; `scrollytelling` is now a valid
// orgSlug match for this route.

import type { RouteSync } from "vike/types";

/**
 * Top-level path segments that belong to other Vike pages or Vike-internal
 * routes. Keep this list in sync when adding new top-level pages under
 * `public/pages/`.
 *
 * NOT included:
 *   - `index` (root `/` only, two-segment match doesn't apply)
 *   - `@handle` (handled by the `@`-prefix check in the regex below)
 *   - `scrollytelling` (real org-slug, see Drift-Resolution above)
 *
 * NOT relevant (handled by nginx before Vike in production):
 *   - `api`, `public`, `assets`, `studio-assets`, `uploads`, `resource`,
 *     `img`, `invitation`, `apiv1`, `scrollys`, `userinfo`
 */
const RESERVED_TOP_SEGMENTS = new Set([
  "s",
  "scrolly",
  "category",
  "preview",
  "__dev__",
  "_error",
]);

export const route: RouteSync = (pageContext) => {
  const url = pageContext.urlPathname ?? "";
  // Match: `/<orgSlug>/<scrollySlug>` with optional trailing slash.
  //   - First segment: starts with non-`@`, non-`/`, non-empty
  //   - Second segment: non-empty
  //   - Optional trailing slash
  //   - No third segment allowed
  const match = url.match(/^\/([^/@][^/]*)\/([^/]+)\/?$/);
  if (!match) return false;
  const orgSlug = match[1];
  const scrollySlug = match[2];
  if (RESERVED_TOP_SEGMENTS.has(orgSlug)) return false;
  // Reject static-file requests — e.g. /svg-examples/zuwanderung-europa.svg.
  // Slugs never end with a file extension; these are asset fetches that would
  // otherwise hit the scrolly data loader and return "Kein Scrolly unter …"
  // HTML as the DOM overlay content for asHtml SVG layers.
  if (/\.[a-z0-9]{1,5}$/i.test(scrollySlug)) return false;
  return {
    routeParams: { orgSlug, scrollySlug },
  };
};
